import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Grid,
  Tabs,
  Tab,
  Avatar,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Card,
  Button,
  keyframes,
} from '@mui/material';
import {
  ArrowBack,
  VideoCall,
  Chat,
  Schedule,
  CheckCircle,
  Cancel,
  Pending,
  CalendarMonth,
  AccessTime,
  Gavel,
  Payment,
  Star,
  Close,
} from '@mui/icons-material';
import clientService from '../../services/clientService';
import { clientLawyerService } from '../../services/clientService';
import RatingDialog from '../../components/UI/RatingDialog';
import { toast } from 'react-toastify';
import { axelionColors } from '../../theme/axelionTheme';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.97);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

/**
 * MaslaXat Premium Consultations Page
 * Elegant, minimalist design with gold accents
 */
const ConsultationsPageGlass = () => {
  const navigate = useNavigate();

  const [currentTab, setCurrentTab] = useState(0);
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedConsultation, setSelectedConsultation] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [ratingDialogOpen, setRatingDialogOpen] = useState(false);
  const [ratingConsultation, setRatingConsultation] = useState(null);

  useEffect(() => {
    fetchConsultations();
  }, []);

  const fetchConsultations = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await clientService.consultations.getConsultations('all');
      setConsultations(data);
    } catch (err) {
      console.error('Error fetching consultations:', err);
      setError('Не удалось загрузить консультации');
      setConsultations([]);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredConsultations = () => {
    if (currentTab === 0) return consultations;
    const statusMap = {
      1: ['accepted', 'pending', 'in_progress'],
      2: ['completed'],
      3: ['cancelled', 'rejected'],
    };
    const allowedStatuses = statusMap[currentTab] || [];
    return consultations.filter(c => allowedStatuses.includes(c.status));
  };

  const handleCancelConsultation = async () => {
    if (!selectedConsultation || !cancelReason.trim()) {
      toast.error('Пожалуйста, укажите причину отмены');
      return;
    }

    try {
      setActionLoading(true);
      await clientService.consultations.cancelConsultation(selectedConsultation.id, cancelReason);
      toast.success('Консультация успешно отменена');
      setCancelDialogOpen(false);
      setCancelReason('');
      setSelectedConsultation(null);
      fetchConsultations();
    } catch (err) {
      console.error('Error canceling consultation:', err);
      toast.error('Не удалось отменить консультацию');
    } finally {
      setActionLoading(false);
    }
  };

  const handleJoinConsultation = async (consultation) => {
    try {
      setActionLoading(true);
      await clientService.consultations.joinConsultation(consultation.id);
      if (consultation.type === 'video') {
        navigate(`/consultations/video/${consultation.id}`);
      } else {
        navigate(`/consultations/chat/${consultation.id}`);
      }
    } catch (err) {
      console.error('Error joining consultation:', err);
      toast.error('Не удалось присоединиться к консультации');
    } finally {
      setActionLoading(false);
    }
  };

  const openCancelDialog = (consultation) => {
    setSelectedConsultation(consultation);
    setCancelDialogOpen(true);
  };

  const closeCancelDialog = () => {
    setCancelDialogOpen(false);
    setCancelReason('');
    setSelectedConsultation(null);
  };

  const openRatingDialog = (consultation) => {
    setRatingConsultation(consultation);
    setRatingDialogOpen(true);
  };

  const handleSubmitRating = async ({ rating, text }) => {
    if (!ratingConsultation) return;
    await clientLawyerService.leaveReview(ratingConsultation.lawyerId || ratingConsultation.lawyer?.id, {
      consultationId: ratingConsultation.id,
      rating,
      text,
    });
    toast.success('Спасибо за отзыв!');
    fetchConsultations();
  };

  const getStatusConfig = (status) => {
    const configs = {
      accepted: {
        label: 'Подтверждено',
        icon: <CheckCircle />,
        bgColor: axelionColors.success,
      },
      pending: {
        label: 'Ожидание',
        icon: <Pending />,
        bgColor: axelionColors.warning,
      },
      in_progress: {
        label: 'В процессе',
        icon: <VideoCall />,
        bgColor: axelionColors.gold,
      },
      completed: {
        label: 'Завершено',
        icon: <CheckCircle />,
        bgColor: axelionColors.gold,
      },
      rejected: {
        label: 'Отклонено',
        icon: <Cancel />,
        bgColor: axelionColors.error,
      },
      cancelled: {
        label: 'Отменено',
        icon: <Cancel />,
        bgColor: axelionColors.error,
      },
    };
    return configs[status] || configs.pending;
  };

  const renderConsultationCard = (consultation, index) => {
    const statusConfig = getStatusConfig(consultation.status);
    const showActions = ['accepted', 'pending', 'in_progress'].includes(consultation.status);

    return (
      <Card
        key={consultation.id}
        sx={{
          mb: { xs: 2, md: 3 },
          p: { xs: 2, md: 3 },
          borderRadius: '8px',
          border: `1px solid ${axelionColors.borderLight}`,
          boxShadow: '0 2px 8px rgba(26, 26, 26, 0.04)',
          background: axelionColors.bgLight,
          animation: `${scaleIn} 0.5s ease-out`,
          animationDelay: `${index * 0.1}s`,
          animationFillMode: 'both',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 8px 24px rgba(26, 26, 26, 0.08)',
            borderColor: axelionColors.gold,
          },
        }}
      >
        <Box>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: { xs: 2, md: 3 }, flexWrap: { xs: 'wrap', sm: 'nowrap' }, gap: { xs: 1, sm: 0 } }}>
            <Box sx={{ display: 'flex', gap: { xs: 1.5, md: 2 }, flexGrow: 1 }}>
              <Avatar
                sx={{
                  width: { xs: 48, md: 64 },
                  height: { xs: 48, md: 64 },
                  bgcolor: axelionColors.bgBeige,
                  color: axelionColors.gold,
                  fontSize: '1.5rem',
                  fontWeight: 300,
                  border: `2px solid ${axelionColors.gold}`,
                }}
              >
                {consultation.lawyer?.name?.charAt(0) || '?'}
              </Avatar>
              <Box sx={{ flexGrow: 1 }}>
                <Typography sx={{ fontWeight: 500, fontSize: '1.1rem', mb: 0.5, color: axelionColors.textDark }}>
                  {consultation.lawyer?.name || 'Юрист'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Chip
                    size="small"
                    label={consultation.lawyer?.profile?.specialization || consultation.lawyer?.specialization || 'Юрист'}
                    icon={<Gavel sx={{ fontSize: 14 }} />}
                    sx={{
                      bgcolor: axelionColors.accentLight,
                      color: axelionColors.gold,
                      fontWeight: 500,
                      fontSize: '0.7rem',
                      borderRadius: '8px',
                      '& .MuiChip-icon': {
                        color: axelionColors.gold,
                      },
                    }}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Star sx={{ fontSize: 16, color: axelionColors.gold }} />
                    <Typography sx={{ fontWeight: 500, fontSize: '0.85rem', color: axelionColors.textDark }}>
                      {consultation.lawyer?.profile?.rating || consultation.lawyer?.rating || 0}
                    </Typography>
                  </Box>
                </Box>
                <Typography sx={{ color: axelionColors.textMuted, fontSize: '0.85rem' }}>
                  {consultation.question || consultation.topic || ''}
                </Typography>
              </Box>
            </Box>
            <Chip
              label={statusConfig.label}
              icon={statusConfig.icon}
              sx={{
                bgcolor: statusConfig.bgColor,
                color: 'white',
                fontWeight: 500,
                fontSize: '0.7rem',
                letterSpacing: '0.05em',
                borderRadius: '8px',
                '& .MuiChip-icon': {
                  color: 'white',
                },
              }}
            />
          </Box>

          {/* Details Grid */}
          <Grid container spacing={2} sx={{ mb: showActions ? 3 : 0 }}>
            {[
              { icon: <CalendarMonth />, label: 'Дата', value: new Date(consultation.preferredDate || consultation.date || consultation.createdAt).toLocaleDateString('ru-RU') },
              { icon: <AccessTime />, label: 'Время', value: consultation.preferredTime || consultation.time || '—' },
              { icon: consultation.type === 'video' ? <VideoCall /> : <Chat />, label: 'Тип', value: consultation.type === 'video' ? 'Видео' : 'Чат' },
              { icon: <Payment />, label: 'Стоимость', value: `${(consultation.price || 0).toLocaleString()} сум`, iconColor: axelionColors.success },
            ].map((item, idx) => (
              <Grid item xs={6} sm={3} key={idx}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: '8px',
                    bgcolor: axelionColors.bgWarm,
                    border: `1px solid ${axelionColors.borderLight}`,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Box sx={{ color: item.iconColor || axelionColors.gold, display: 'flex' }}>
                      {React.cloneElement(item.icon, { sx: { fontSize: 18 } })}
                    </Box>
                    <Typography
                      sx={{
                        color: axelionColors.textMuted,
                        fontWeight: 500,
                        fontSize: '0.65rem',
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                      }}
                    >
                      {item.label}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontWeight: 500, color: axelionColors.textDark, fontSize: '0.85rem' }}>
                    {item.value}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Action Buttons */}
          {showActions && (
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {['accepted', 'in_progress'].includes(consultation.status) && (
                <>
                  <Button
                    startIcon={consultation.type === 'video' ? <VideoCall /> : <Chat />}
                    onClick={() => handleJoinConsultation(consultation)}
                    disabled={actionLoading}
                    sx={{
                      bgcolor: axelionColors.gold,
                      color: 'white',
                      fontWeight: 500,
                      textTransform: 'uppercase',
                      letterSpacing: '0.1em',
                      fontSize: '0.75rem',
                      borderRadius: '8px',
                      px: 3,
                      py: 1,
                      boxShadow: 'none',
                      '&:hover': {
                        bgcolor: axelionColors.goldDark,
                        boxShadow: 'none',
                      },
                    }}
                  >
                    {consultation.type === 'video' ? 'Войти в видео' : 'Открыть чат'}
                  </Button>
                  <Button
                    startIcon={<Cancel />}
                    onClick={() => openCancelDialog(consultation)}
                    disabled={actionLoading}
                    sx={{
                      color: axelionColors.error,
                      border: `1px solid ${axelionColors.borderLight}`,
                      fontWeight: 500,
                      textTransform: 'uppercase',
                      letterSpacing: '0.1em',
                      fontSize: '0.75rem',
                      borderRadius: '8px',
                      px: 3,
                      py: 1,
                      '&:hover': {
                        borderColor: axelionColors.error,
                        bgcolor: axelionColors.errorLight,
                      },
                    }}
                  >
                    Отменить
                  </Button>
                </>
              )}
              {consultation.status === 'pending' && (
                <Button
                  startIcon={<Cancel />}
                  onClick={() => openCancelDialog(consultation)}
                  disabled={actionLoading}
                  sx={{
                    color: axelionColors.error,
                    border: `1px solid ${axelionColors.borderLight}`,
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    fontSize: '0.75rem',
                    borderRadius: '8px',
                    px: 3,
                    py: 1,
                    '&:hover': {
                      borderColor: axelionColors.error,
                      bgcolor: axelionColors.errorLight,
                    },
                  }}
                >
                  Отменить запрос
                </Button>
              )}
            </Box>
          )}

          {/* Rate button for completed consultations */}
          {consultation.status === 'completed' && !consultation.rating && (
            <Box sx={{ mt: 2 }}>
              <Button
                startIcon={<Star />}
                onClick={() => openRatingDialog(consultation)}
                sx={{
                  bgcolor: axelionColors.gold,
                  color: 'white',
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  fontSize: '0.75rem',
                  borderRadius: '8px',
                  px: 3,
                  py: 1,
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: axelionColors.goldDark,
                    boxShadow: 'none',
                  },
                }}
              >
                Оценить консультацию
              </Button>
            </Box>
          )}
        </Box>
      </Card>
    );
  };

  const getTabCounts = () => {
    return {
      all: consultations.length,
      upcoming: consultations.filter(c => ['accepted', 'pending', 'in_progress'].includes(c.status)).length,
      completed: consultations.filter(c => c.status === 'completed').length,
      cancelled: consultations.filter(c => ['cancelled', 'rejected'].includes(c.status)).length,
    };
  };

  const tabCounts = getTabCounts();
  const filteredConsultations = getFilteredConsultations();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: axelionColors.bgCream, pb: 6 }}>
      {/* Header */}
      <Box
        sx={{
          bgcolor: axelionColors.bgLight,
          borderBottom: `1px solid ${axelionColors.borderLight}`,
          py: { xs: 2, md: 4 },
          px: { xs: 1, md: 2 },
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2 } }}>
              <IconButton
                onClick={() => navigate('/dashboard')}
                sx={{
                  bgcolor: axelionColors.bgWarm,
                  border: `1px solid ${axelionColors.borderLight}`,
                  borderRadius: '8px',
                  '&:hover': {
                    bgcolor: axelionColors.bgBeige,
                    borderColor: axelionColors.gold,
                  },
                }}
              >
                <ArrowBack sx={{ color: axelionColors.textDark }} />
              </IconButton>
              <Box>
                <Typography
                  sx={{
                    fontWeight: 300,
                    fontSize: { xs: '1.1rem', md: '1.5rem' },
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: axelionColors.textDark,
                    mb: 0.5,
                  }}
                >
                  My Consultations
                </Typography>
                <Typography sx={{ color: axelionColors.textMuted, fontSize: '0.85rem', display: { xs: 'none', sm: 'block' } }}>
                  Управляйте своими юридическими консультациями
                </Typography>
              </Box>
            </Box>
            <Button
              startIcon={<Schedule />}
              onClick={() => navigate('/lawyers')}
              sx={{
                bgcolor: axelionColors.gold,
                color: 'white',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                fontSize: { xs: '0.65rem', md: '0.75rem' },
                borderRadius: '8px',
                px: { xs: 2, md: 3 },
                py: 1.5,
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: axelionColors.goldDark,
                  boxShadow: 'none',
                },
              }}
            >
              Book New
            </Button>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ mt: 4 }}>
        {/* Tabs */}
        <Card
          sx={{
            mb: 4,
            borderRadius: '8px',
            border: `1px solid ${axelionColors.borderLight}`,
            boxShadow: '0 2px 8px rgba(26, 26, 26, 0.04)',
            bgcolor: axelionColors.bgLight,
            animation: `${fadeInUp} 0.5s ease-out`,
          }}
        >
          <Tabs
            value={currentTab}
            onChange={(e, val) => setCurrentTab(val)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                fontWeight: 500,
                fontSize: '0.8rem',
                letterSpacing: '0.05em',
                minHeight: 64,
                color: axelionColors.textMuted,
                textTransform: 'uppercase',
              },
              '& .Mui-selected': {
                color: axelionColors.gold,
              },
              '& .MuiTabs-indicator': {
                bgcolor: axelionColors.gold,
                height: 2,
              },
            }}
          >
            <Tab label={`Все (${tabCounts.all})`} icon={<Schedule />} iconPosition="start" />
            <Tab label={`Предстоящие (${tabCounts.upcoming})`} icon={<Pending />} iconPosition="start" />
            <Tab label={`Завершенные (${tabCounts.completed})`} icon={<CheckCircle />} iconPosition="start" />
            <Tab label={`Отмененные (${tabCounts.cancelled})`} icon={<Cancel />} iconPosition="start" />
          </Tabs>
        </Card>

        {/* Content */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                border: `2px solid ${axelionColors.borderLight}`,
                borderTopColor: axelionColors.gold,
                animation: 'spin 1s linear infinite',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                },
              }}
            />
          </Box>
        ) : error ? (
          <Alert
            severity="error"
            sx={{
              borderRadius: '8px',
              bgcolor: axelionColors.errorLight,
              border: `1px solid ${axelionColors.error}30`,
              animation: `${fadeInUp} 0.5s ease-out`,
            }}
          >
            {error}
          </Alert>
        ) : filteredConsultations.length > 0 ? (
          <Box>
            {filteredConsultations.map((consultation, index) =>
              renderConsultationCard(consultation, index)
            )}
          </Box>
        ) : (
          <Card
            sx={{
              textAlign: 'center',
              py: 8,
              px: 3,
              borderRadius: '8px',
              border: `1px solid ${axelionColors.borderLight}`,
              boxShadow: '0 2px 8px rgba(26, 26, 26, 0.04)',
              bgcolor: axelionColors.bgLight,
              animation: `${fadeInUp} 0.5s ease-out`,
            }}
          >
            <Schedule sx={{ fontSize: 80, color: axelionColors.gold, mb: 2, opacity: 0.3 }} />
            <Typography
              sx={{
                fontWeight: 300,
                fontSize: '1.25rem',
                letterSpacing: '0.1em',
                color: axelionColors.textDark,
                mb: 1,
              }}
            >
              No Consultations Found
            </Typography>
            <Typography sx={{ color: axelionColors.textMuted, mb: 3 }}>
              У вас пока нет консультаций в этой категории
            </Typography>
            <Button
              startIcon={<Schedule />}
              onClick={() => navigate('/lawyers')}
              sx={{
                bgcolor: axelionColors.gold,
                color: 'white',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                fontSize: '0.75rem',
                borderRadius: '8px',
                px: 3,
                py: 1.5,
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: axelionColors.goldDark,
                  boxShadow: 'none',
                },
              }}
            >
              Find a Lawyer
            </Button>
          </Card>
        )}
      </Container>

      {/* Rating Dialog */}
      <RatingDialog
        open={ratingDialogOpen}
        onClose={() => { setRatingDialogOpen(false); setRatingConsultation(null); }}
        onSubmit={handleSubmitRating}
        lawyerName={ratingConsultation?.lawyer?.name}
      />

      {/* Cancel Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={closeCancelDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '8px',
            border: `1px solid ${axelionColors.borderLight}`,
            boxShadow: '0 24px 48px rgba(26, 26, 26, 0.15)',
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography
              sx={{
                fontWeight: 300,
                fontSize: '1.25rem',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: axelionColors.textDark,
              }}
            >
              Cancel Consultation
            </Typography>
            <IconButton onClick={closeCancelDialog} size="small">
              <Close sx={{ color: axelionColors.textMuted }} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedConsultation && (
            <Box sx={{ mb: 3, p: 2, borderRadius: '8px', bgcolor: axelionColors.bgWarm, border: `1px solid ${axelionColors.borderLight}` }}>
              <Typography sx={{ color: axelionColors.textMuted, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.1em' }} gutterBottom>
                Консультация с:
              </Typography>
              <Typography sx={{ fontWeight: 500, color: axelionColors.textDark }}>
                {selectedConsultation.lawyer.name}
              </Typography>
              <Typography sx={{ color: axelionColors.textMuted, fontSize: '0.85rem' }}>
                {new Date(selectedConsultation.date).toLocaleDateString('ru-RU')} в {selectedConsultation.time}
              </Typography>
            </Box>
          )}
          <TextField
            label="Причина отмены"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            multiline
            rows={4}
            fullWidth
            placeholder="Пожалуйста, укажите причину отмены консультации..."
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                '& fieldset': {
                  borderColor: axelionColors.borderLight,
                },
                '&:hover fieldset': {
                  borderColor: axelionColors.gold,
                },
                '&.Mui-focused fieldset': {
                  borderColor: axelionColors.gold,
                  borderWidth: 1,
                },
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: axelionColors.gold,
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={closeCancelDialog}
            sx={{
              color: axelionColors.textSecondary,
              border: `1px solid ${axelionColors.borderLight}`,
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              fontSize: '0.75rem',
              borderRadius: '8px',
              px: 3,
              py: 1,
              '&:hover': {
                borderColor: axelionColors.textSecondary,
                bgcolor: axelionColors.bgWarm,
              },
            }}
          >
            Back
          </Button>
          <Button
            onClick={handleCancelConsultation}
            disabled={!cancelReason.trim() || actionLoading}
            startIcon={<Cancel />}
            sx={{
              bgcolor: axelionColors.error,
              color: 'white',
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              fontSize: '0.75rem',
              borderRadius: '8px',
              px: 3,
              py: 1,
              boxShadow: 'none',
              '&:hover': {
                bgcolor: '#9A5A5A',
                boxShadow: 'none',
              },
              '&:disabled': {
                bgcolor: axelionColors.bgBeige,
                color: axelionColors.textMuted,
              },
            }}
          >
            Cancel Consultation
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConsultationsPageGlass;
